/**
 * Structured edit output for refinement turns.
 *
 * Instead of having Claude regenerate the full HTML document every time the user
 * makes a small change (which is what was making 30–50 KB portfolio edits take
 * 80–150s on Sonnet), the model emits a small `<edits>...</edits>` block of DOM
 * operations and the host applies them to the prior version's HTML in milliseconds.
 *
 * Output goes from ~14k tokens to a few hundred. Wall time goes from minutes to
 * ~5–10 seconds (mostly TTFT).
 *
 * Format the model emits:
 *
 *   Brief one-sentence summary of what's changing.
 *
 *   <edits>
 *     <edit selector="..." action="remove" />
 *     <edit selector="..." action="replace"><![CDATA[<div>new</div>]]></edit>
 *     <edit selector="..." action="set-text">New title</edit>
 *     <edit selector="..." action="set-attr" attr="class" value="primary" />
 *     <edit selector="..." action="append"><![CDATA[<li>added</li>]]></edit>
 *     <edit selector="..." action="prepend"><![CDATA[<header>added</header>]]></edit>
 *     <edit selector="..." action="before"><![CDATA[<div>sibling</div>]]></edit>
 *     <edit selector="..." action="after"><![CDATA[<div>sibling</div>]]></edit>
 *   </edits>
 *
 * If the request needs full restructuring the model can still emit `<artifact>...`
 * and the host falls back to full-document regeneration — see useApp.ts.
 */

export type EditOp =
  | { action: 'remove'; selector: string }
  | { action: 'replace'; selector: string; html: string }
  | { action: 'set-text'; selector: string; text: string }
  | { action: 'set-attr'; selector: string; attr: string; value: string }
  | { action: 'append'; selector: string; html: string }
  | { action: 'prepend'; selector: string; html: string }
  | { action: 'before'; selector: string; html: string }
  | { action: 'after'; selector: string; html: string }
  /** Raw string substitution across the entire HTML source (including <script> blocks). */
  | { action: 'find-replace'; find: string; replace: string };

const EDITS_BLOCK_RE = /<edits\b[^>]*>([\s\S]*?)<\/edits>/i;

/** Pull out the inner text of the first `<edits>...</edits>` block, or null. */
export function extractEditsBlock(text: string): string | null {
  const match = EDITS_BLOCK_RE.exec(text);
  return match && match[1] !== undefined ? match[1] : null;
}

/**
 * Strip any `<edits>` block from the chat-displayable text (used during streaming
 * AND after) so the user sees only Claude's human-readable summary, not the raw
 * XML payload.
 *
 * Handles three cases:
 *   - Complete block: removed entirely.
 *   - Open tag with no close yet (mid-stream): everything from the open tag is hidden.
 */
export function stripEditsBlock(text: string): string {
  let s = text.replace(/<edits\b[^>]*>[\s\S]*?<\/edits>/gi, '');
  s = s.replace(/<edits\b[^>]*>[\s\S]*$/i, '');
  return s.trim();
}

const ATTR_RE = /([A-Za-z_][\w-]*)\s*=\s*"([^"]*)"/g;
// Matches either `<edit attrs />` (self-closing) or `<edit attrs>content</edit>` (paired).
//
// The attrs section is matched as a sequence of `name="value"` pairs rather than
// `[^>]*?`, because a CSS selector value frequently contains `>` (the descendant
// combinator) — and a `[^>]` class would terminate the attrs scan inside the quoted
// selector value, silently dropping the entire edit.
const TAG_RE =
  /<edit\b((?:\s+[A-Za-z_][\w-]*="[^"]*")*)\s*(?:\/>|>([\s\S]*?)<\/edit\s*>)/g;

function parseAttrs(attrStr: string): Record<string, string> {
  const out: Record<string, string> = {};
  const re = new RegExp(ATTR_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(attrStr)) !== null) {
    out[m[1]!] = m[2]!;
  }
  return out;
}

function unwrapPayload(raw: string): string {
  const cdata = /^\s*<!\[CDATA\[([\s\S]*)\]\]>\s*$/m.exec(raw);
  if (cdata && cdata[1] !== undefined) return cdata[1];
  return raw
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');
}

/**
 * Parse an `<edit>` payload into structured ops. Malformed entries are skipped.
 * Implementation note: hand-rolled regex rather than DOMParser because XML+CDATA
 * support is inconsistent across happy-dom (test env) and some browsers, and the
 * format is simple enough to handle directly.
 */
export function parseEdits(editsBody: string): EditOp[] {
  const ops: EditOp[] = [];
  const re = new RegExp(TAG_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(editsBody)) !== null) {
    const attrs = parseAttrs(m[1] ?? '');
    const rawContent = m[2]; // undefined for self-closing
    const action = attrs.action?.trim();
    if (!action) continue;

    // find-replace is selector-free — handle before the selector guard.
    if (action === 'find-replace') {
      const find = attrs.find;
      if (find !== undefined) {
        ops.push({ action: 'find-replace', find, replace: attrs.replace ?? '' });
      }
      continue;
    }

    const selector = attrs.selector?.trim();
    if (!selector) continue;

    switch (action) {
      case 'remove':
        ops.push({ action: 'remove', selector });
        break;
      case 'replace':
      case 'append':
      case 'prepend':
      case 'before':
      case 'after':
        ops.push({
          action,
          selector,
          html: rawContent !== undefined ? unwrapPayload(rawContent).trim() : '',
        });
        break;
      case 'set-text':
        ops.push({
          action: 'set-text',
          selector,
          text: rawContent !== undefined ? unwrapPayload(rawContent) : '',
        });
        break;
      case 'set-attr': {
        const attr = attrs.attr?.trim();
        if (attr) {
          ops.push({
            action: 'set-attr',
            selector,
            attr,
            value: attrs.value ?? '',
          });
        }
        break;
      }
      default:
        // Unknown action — skip rather than fail the whole batch.
        break;
    }
  }
  return ops;
}

export interface ApplyEditsResult {
  html: string;
  applied: number;
  skipped: Array<{ op: EditOp; reason: string }>;
}

/** Apply ops to an HTML document string and return the serialized result. */
export function applyEdits(html: string, ops: EditOp[]): ApplyEditsResult {
  let applied = 0;
  const skipped: ApplyEditsResult['skipped'] = [];

  // find-replace runs on the raw HTML string before DOMParser so it reaches
  // text inside <script> blocks, <title>, attribute values, and all elements.
  let workingHtml = html;
  for (const op of ops) {
    if (op.action !== 'find-replace') continue;
    if (!op.find) {
      skipped.push({ op, reason: 'Empty find string' });
      continue;
    }
    const next = workingHtml.split(op.find).join(op.replace);
    if (next === workingHtml) {
      skipped.push({ op, reason: `Text not found: "${op.find}"` });
    } else {
      workingHtml = next;
      applied++;
    }
  }

  const doc = new DOMParser().parseFromString(workingHtml, 'text/html');

  for (const op of ops) {
    if (op.action === 'find-replace') continue; // already handled in the pre-pass above
    let target: Element | null = null;
    try {
      target = doc.querySelector(op.selector);
    } catch {
      skipped.push({ op, reason: `Invalid selector: ${op.selector}` });
      continue;
    }
    if (!target) {
      skipped.push({ op, reason: `Selector did not match: ${op.selector}` });
      continue;
    }
    try {
      switch (op.action) {
        case 'remove':
          target.remove();
          break;
        case 'replace':
          target.outerHTML = op.html;
          break;
        case 'set-text':
          target.textContent = op.text;
          break;
        case 'set-attr':
          target.setAttribute(op.attr, op.value);
          break;
        case 'append':
          target.insertAdjacentHTML('beforeend', op.html);
          break;
        case 'prepend':
          target.insertAdjacentHTML('afterbegin', op.html);
          break;
        case 'before':
          target.insertAdjacentHTML('beforebegin', op.html);
          break;
        case 'after':
          target.insertAdjacentHTML('afterend', op.html);
          break;
      }
      applied++;
    } catch (err) {
      skipped.push({
        op,
        reason: err instanceof Error ? err.message : 'Apply failed',
      });
    }
  }

  // DOMParser drops the doctype off serialization; preserve it.
  const doctype = doc.doctype ? `<!DOCTYPE ${doc.doctype.name}>\n` : '<!DOCTYPE html>\n';
  return {
    html: doctype + doc.documentElement.outerHTML,
    applied,
    skipped,
  };
}
